
import { Signal } from "signals";
import Components from "slotworks/components/components";
import ReelComponent from "slotworks/components/matrix/reel/reel-component";
import SymbolComponent from "slotworks/components/matrix/symbol/symbol-component";
import AnimatedSprite from "slotworks/graphics/animation/animated-sprite";
import Layers from "slotworks/graphics/layers/layers";
import SlotDefinition from "slotworks/model/slot-definition";
import { SoundEvent } from "slotworks/sound/sound-events";
import SoundManager from "slotworks/sound/sound-manager";
import { wrapIndex } from "slotworks/utils/collection-utils";
import Contract from "slotworks/utils/contract";
import Point from "slotworks/utils/geom/point";
import Rectangle from "slotworks/utils/geom/rectangle";
import Timer from "slotworks/utils/timer";
import canvasManager, { Orientation } from "slotworks/graphics/canvas/canvas-manager";

export default class ReelSpinner {

    public static spinStages: SpinStage[] = [
        { name: "pullback", s: 0.2, t: 250 } as SpinStage,
        { name: "wait", s: -0, t: 200 } as SpinStage,
        { name: "accelerate", s: -1.2, u: 0, v: -0.01 } as SpinStage,
        { name: "minSpin", s: -1, u: -0.01, v: -0.01 } as SpinStage,
        { name: "repeat", s: -1, u: -0.01, v: -0.01 } as SpinStage,
        { name: "stop", s: -4.2, u: -0.01, v: 0 } as SpinStage,
        { name: "complete", s: 0.2, t: 250 } as SpinStage
    ];

    public static skipStages: SpinStage[] = [
        { name: "stop", s: -4.2, t: 500 } as SpinStage,
        { name: "complete", s: 0.2, t: 250 } as SpinStage
    ];

    public static anticipationStages: SpinStage[] = [
        { name: "anticipate", s: -8, u: -0.01, v: -0.01 } as SpinStage,
        { name: "stop", s: -12, u: -0.01, v: 0 } as SpinStage
    ];

    /**
     * Override the layer the anticipation animations are searched for in (SymbolAnimations by default)
     */
    public static anticipationLayer: Layers | Layers[];
    public static minRepeats = 7;
    /**
     * Extends reel to accomodate oversizes symbols overhanging
     */
    public static extended: boolean = false;

    public landSignal: Signal = new Signal();
    public anticipateStartSignal: Signal = new Signal();
    public onMove: Signal = new Signal();

    private reel: ReelComponent;

    private allSymbols: SymbolComponent[];
    private symbolPositions: Rectangle[];

    private topExtendedHiddenSymbol: SymbolComponent;
    private topHiddenSymbol: SymbolComponent;
    private bottomHiddenSymbol: SymbolComponent;

    private reelstrip: string[];

    private result: { index: number, reelstrip: string[] };

    // Configs
    private stages: SpinStage[];

    // State
    private animating: boolean;
    private started: boolean;
    private stage: number = 0;
    private stopTimeout: number;
    private stopping: boolean;
    private waitingToStop: boolean;
    private repeatCount: number = 0;
    private currentOrientation:Orientation;

    // Motion variables
    private startTime: number;
    private target: SpinStage;
    // Current position is somewhere between where a stage starts and ends, depending on time
    private currentPosition: number = 0;
    // Fixed position is where the reel should be between stages
    private fixedPosition: number = 0;
    // The index on the reelstrip the top most symbol is currently displaying
    private topSymbolIndex: number = 0;
    // The last distance update
    private lastDistance: number = 0;

    // Contract resolves
    private startedResolve: Function;
    private completeResolve: Function;

    private anticipationAnimations: AnimatedSprite[];

    constructor(reel: ReelComponent, reelstop: number, reelstrip: string[]) {

        this.reel = reel;

        this.reelstrip = [...reelstrip];

        this.fixedPosition = this.currentPosition = reelstop;

        this.getSymbolPositions();
        this.addHiddenSymbols();
        if (ReelSpinner.extended) {
            this.enableHiddenSymbols();
        } else {
            this.disableHiddenSymbols();
        }
        this.updateSymbolPositions();
        Components.matrix.flush();
    }

    /**
     * Start the reel spinning using last reel strip (either from result or from constructor)
     */
    public start(delay: number = 0, jumpStart: boolean = false): Contract<void> {
        this.currentOrientation = canvasManager.orientation;
        this.getSymbolPositions();
        this.enableHiddenSymbols();

        this.started = false;

        Timer.setTimeout(() => {

            SoundManager.event(SoundEvent.reel_N_spin_start, this.reel.index.toString());
            SoundManager.event(SoundEvent.reel_any_spin_start);

            this.startTime = Timer.time;
            this.target = { s: 0, t: 0, u: 0, v: 0, a: 0 } as SpinStage;
            this.lastDistance = 0;
            this.currentPosition = 0;

            if (this.result) {
                this.fixedPosition = this.result.index;
                this.reelstrip = [...this.result.reelstrip];
            }

            this.log("MS:", this.reel.index, this.fixedPosition, this.reelstrip);

            // Update reelstrip to include current symbols in view (which may have changed due to an expand etc)
            for (let y = -1; y < this.reel.size + 1; y++) {
                let symbolId;
                if (y < 0) {
                    if (!ReelSpinner.extended) {
                        continue;
                    }
                    symbolId = this.topHiddenSymbol.symbolId;
                } else if (y >= this.reel.size) {
                    if (!ReelSpinner.extended) {
                        continue;
                    }
                    symbolId = this.bottomHiddenSymbol.symbolId;
                } else {
                    symbolId = this.reel.getSymbolAt(y).symbolId;
                }

                let reelStripIndex = this.fixedPosition + y;
                if (reelStripIndex < 0) {
                    reelStripIndex += this.reelstrip.length;
                }

                this.reelstrip[reelStripIndex] = symbolId;
            }

            // Check strip for stacks
            const topSymbol = this.reelstrip[this.fixedPosition];
            const topSymbolDef = SlotDefinition.getSymbolDefinition(topSymbol);
            if (topSymbolDef.size > 1) {
                let stackSize = 1;
                for (let y = 1; y < topSymbolDef.size; y++) {
                    const nextSymbol = this.reelstrip[this.fixedPosition + y];
                    if (nextSymbol === topSymbol) {
                        stackSize++;
                    }
                }

                // Remove unneeded stack continuations above
                let checkIndex = this.fixedPosition;
                for (let y = 0; y < topSymbolDef.size; y++) {
                    checkIndex--;
                    if (checkIndex < 0) {
                        checkIndex += this.reelstrip.length;
                    }

                    // Found stack continuation
                    if (this.reelstrip[checkIndex] === topSymbol || "expanded_" + this.reelstrip[checkIndex] === topSymbol) {
                        if (stackSize < topSymbolDef.size) {
                            stackSize++;
                            continue;
                        }
                        this.log("MS:", this.reel.index, "CLEAN UP CONTINUATIONS START", this.reelstrip);
                        this.reelstrip.splice(checkIndex, 1);
                        if (this.fixedPosition > checkIndex) {
                            this.fixedPosition--;
                        }
                        this.log("MS:", this.reel.index, "CLEAN UP CONTINUATIONS DONE", this.reelstrip);
                    } else {
                        break;
                    }
                }
            }

            this.stages = ReelSpinner.spinStages;
            this.stage = 0;

            if (jumpStart) {
                for (let stageIndex = 0; stageIndex < this.stages.length; stageIndex++) {
                    const stage = this.stages[stageIndex];
                    if (stage.name === "repeat") {
                        this.stage = stageIndex;
                    }
                }
            }

            this.animating = true;

            this.nextStage();
        }, delay);

        return new Contract<void>((resolve) => {
            this.startedResolve = () => resolve(null);
        });
    }

    /**
     * Stops the reel on the specified index within the specified reelstrip
     *
     * @param index {number}
     * @param reelstrip {string[]}
     */
    public stop(index: number, reelstrip: string[], delay: number = 0, quickSpin: boolean): Contract<void> {

        if (!this.stopping) {

            if(quickSpin) 
                this.repeatCount = ReelSpinner.minRepeats;
            else
                this.repeatCount = Math.max(this.repeatCount, Math.ceil(ReelSpinner.minRepeats * 0.5));

            const doStop = () => {
                if (!this.waitingToStop) {
                    this.result = { index, reelstrip };
                    this.waitingToStop = true;
                }
            };

            if (delay > 0) {
                this.stopTimeout = Timer.setTimeout(doStop, delay);
            } else {
                doStop();
            }
        }

        return new Contract<void>((resolve) => {
            this.completeResolve = () => {
                SoundManager.event(SoundEvent.reel_N_spin_end, this.reel.index.toString());
                SoundManager.event(SoundEvent.reel_any_spin_end);
                resolve(null);
            };

            if (!this.animating) {
                resolve(null);
            }
        });
    }

    /**
     * Quickly stops the reel on the specified index within the specified reelstrip
     *
     * @param index {number}
     * @param reelstrip {string[]}
     */
    public skip(index: number, reelstrip: string[]): Contract<void> {

        if (this.animating && !this.stopping) {
            this.repeatCount = ReelSpinner.minRepeats;
            this.waitingToStop = false;
            this.stopping = true;

            Timer.clearTimeout(this.stopTimeout);

            SoundManager.event(SoundEvent.reel_N_skip_start, this.reel.index.toString());
            SoundManager.event(SoundEvent.reel_any_skip_start);

            this.result = { index, reelstrip };

            this.stages = ReelSpinner.skipStages.concat();
            this.stage = 0;

            const distanceToWhole = Math.ceil(this.currentPosition - 1) - this.currentPosition;

            this.stages[0] = { ...this.stages[0] };

            const distanceToGo = this.target.s - (this.currentPosition - this.fixedPosition);

            if (this.target.name === "stop" && Math.abs(distanceToGo) < Math.abs(this.stages[0].s)) {
                const difference = Math.floor(this.stages[0].s) - Math.floor(distanceToGo);
                this.stages[0].s -= difference;
            }

            this.stages[0].s += distanceToWhole;

            this.fixedPosition = this.currentPosition;
            this.currentPosition = 0;
            this.startTime = Timer.time;
            this.lastDistance = 0;
            this.target = { s: 0, t: 0, u: 0, v: 0, a: 0 } as SpinStage;

            this.nextStage();
        }

        return new Contract<void>((resolve) => {
            this.completeResolve = () => {
                SoundManager.event(SoundEvent.reel_N_skip_end, this.reel.index.toString());
                SoundManager.event(SoundEvent.reel_any_skip_end);
                resolve(null);
            };

            if (!this.animating) {
                resolve(null);
            }
        });
    }

    /**
     * Slowly stops the reel on the specified index within the specified reelstrip
     *
     * @param index {number}
     * @param reelstrip {string[]}
     */
    public anticipate(index: number, reelstrip: string[]): Contract<void> {
        const anticipationLayers = this.getAnticipationLayers();

        this.anticipationAnimations = [];
        for (const layer of anticipationLayers) {
            const anticipationAnimation = layer.getAnimatedSprite("anticipation_" + this.reel.index);
            this.anticipateStartSignal.dispatch(this.reel.index);
            if (anticipationAnimation) {
                anticipationAnimation.visible = true;
                anticipationAnimation.loop = true;
                anticipationAnimation.play();
                this.anticipationAnimations.push(anticipationAnimation);
            }
        }

        SoundManager.event(SoundEvent.reel_N_anticipate_start, this.reel.index.toString());
        SoundManager.event(SoundEvent.reel_any_anticipate_start);

        this.result = { index, reelstrip };

        this.stages = ReelSpinner.anticipationStages.concat();
        this.stage = 0;

        const distanceToWhole = Math.ceil(this.currentPosition - 1) - this.currentPosition;

        this.stages[0] = { ...this.stages[0] };
        this.stages[0].s += distanceToWhole;

        this.fixedPosition = this.currentPosition;
        this.currentPosition = 0;
        this.startTime = Timer.time;
        this.lastDistance = 0;
        this.target = { s: 0, t: 0, u: 0, v: 0, a: 0 } as SpinStage;

        this.nextStage();

        return new Contract<void>((resolve) => {
            this.completeResolve = () => {

                SoundManager.event(SoundEvent.reel_N_anticipate_end, this.reel.index.toString());
                SoundManager.event(SoundEvent.reel_any_anticipate_end);

                resolve(null);
            };

            if (!this.animating) {
                resolve(null);
            }
        });
    }

    public update() {

        if (this.animating) {

            const elapsedTime = (Timer.time - this.startTime);

            this.move(elapsedTime);

            if (elapsedTime >= this.target.t) {

                this.nextStage();
            }
        }

    }

    /**
     * If the reels jump, we have to update the result the spinner thinks it's looking at
     */
    public jump(index: number, reelstrip: string[]) {
        this.reset();
        this.result = { index, reelstrip };
    }

    public isStopping() {
        return this.stopping;
    }

    private spinComplete() {

        this.reset();

        this.completeResolve();
    }

    private reset() {
        this.currentPosition = this.fixedPosition;
        this.updateSymbolPositions();

        this.repeatCount = 0;
        this.animating = false;
        this.waitingToStop = false;
        this.stopping = false;

        if (this.anticipationAnimations) {
            for (const anticipationAnimation of this.anticipationAnimations) {
                if (anticipationAnimation) {
                    anticipationAnimation.stop();
                    anticipationAnimation.visible = false;
                }
                this.anticipationAnimations = null;
            }
        }

        this.disableHiddenSymbols();
    }

    private move(elapsedTime: number) {

        const distance = (this.target.u * elapsedTime) + (0.5 * this.target.a) * (elapsedTime * elapsedTime);

        this.currentPosition = this.fixedPosition + distance;

        this.onMove.dispatch(distance - this.lastDistance);
        this.lastDistance = distance;

        this.updateSymbolPositions();
    }

    private nextStage() {
        if (this.stage > 0) {
            // Update fixed position
            this.fixedPosition += this.target.s;

            // Fix floating point errors
            const diff = Math.abs(this.fixedPosition - Math.round(this.fixedPosition));
            if (diff > 0 && diff < 0.01) {
                this.fixedPosition = Math.round(this.fixedPosition);
            }

            // Wrap position
            while (this.fixedPosition >= this.reelstrip.length) {
                this.fixedPosition -= this.reelstrip.length;
            }
            while (this.fixedPosition < 0) {
                this.fixedPosition += this.reelstrip.length;
            }
        }

        if (this.stage === this.stages.length) {
            this.spinComplete();
        } else {
            const currentStage = this.stages[this.stage];
            const previousStage = this.stages[this.stage - 1];

            SoundManager.event(SoundEvent.spinner_stage_complete, currentStage.name);

            // Lands
            if (previousStage && previousStage.name === "stop") {
                this.stopping = true;
                this.landSignal.dispatch(this.reel.index);
            }

            this.setTarget(currentStage);

            if (currentStage.name !== "repeat" || this.waitingToStop) {
                if (currentStage.name === "repeat") {
                    this.repeatCount++;
                    if (!this.stopping && this.repeatCount >= ReelSpinner.minRepeats) {
                        this.stopping = true;
                    } else {
                        this.stage--;
                    }
                }

                this.stage++;
            }

            if (currentStage.name === "repeat" && !this.started) {
                this.started = true;
                this.startedResolve();
            }
        }
    }

    private setTarget(target: SpinStage) {

        target = { ...target };

        if (target.name === "stop") {
            // Finish off all stacks before spinning in results
            target.s -= (this.reel.size - 1) * 2 + 1;
        }

        const overlapTime = (Timer.time - this.startTime) - this.target.t;
        const overshootDistance = this.currentPosition - this.target.s;

        if (target.t) {
            target.a = 0;
            target.u = target.v = target.s / target.t;
        } else {
            target.a = ((target.v * target.v) - (target.u * target.u)) / (2 * target.s);
            target.t = Math.abs((target.v !== target.u) ? ((target.v - target.u) / target.a) : (target.s / target.u));
        }

        this.currentPosition = overshootDistance;
        this.target = target;
        this.startTime = Timer.time - overlapTime;

        if (this.stage >= this.stages.length - 1) {
            this.currentPosition = 0;
        }

        if (target.name === "stop") {
            this.spliceInResults();
        }

        this.lastDistance = (this.target.u * overlapTime) + (0.5 * this.target.a) * (overlapTime * overlapTime);
        this.move(overlapTime);
    }

    private spliceInResults() {

        const resultsToSpliceIn: string[] = [];

        const thisIndex = Math.floor(this.fixedPosition);
        const targetIndex = Math.ceil(thisIndex + this.target.s) - 1;

        const numberOfSymbols = Math.abs(targetIndex - thisIndex);

        if (this.target.s < 0) {

            for (let i = 0; i < numberOfSymbols; i++) {
                resultsToSpliceIn.splice(i, 0, wrapIndex(this.result.index + i - 1, this.result.reelstrip));
            }

            this.log("MS:", this.reel.index, "STRIP BEFORE", this.reelstrip, this.fixedPosition);

            this.generateResultBuffer(resultsToSpliceIn);

            let pushIndex = Math.floor(this.fixedPosition);

            if (pushIndex < 0) {
                pushIndex += this.reelstrip.length;
            } else {
                this.fixedPosition += numberOfSymbols;
            }

            this.reelstrip = [...this.reelstrip];

            while (resultsToSpliceIn.length) {
                this.reelstrip.splice(pushIndex, 0, resultsToSpliceIn.pop());
            }
            this.log("MS:", this.reel.index, "STRIP AFTER", this.reelstrip, this.fixedPosition);

        } else {
            // Backward spinning likely doesn't work well
            const nextPosition = Math.floor(this.fixedPosition) + this.reel.size;

            for (let i = 0; i < numberOfSymbols + 1; i++) {
                resultsToSpliceIn.push(wrapIndex(this.result.index - this.reel.size + 1 + i, this.result.reelstrip));
            }

            this.reelstrip = [...this.reelstrip.slice(0, nextPosition), ...resultsToSpliceIn, ...this.reelstrip.slice(nextPosition)];
        }
    }

    private generateResultBuffer(incoming: string[]) {
        this.log("MS:", this.reel.index, "Incoming", incoming);
        let randomBufferStart = this.reel.size + 1;
        let randomBufferEnd = incoming.length;

        // If stacks are present before or after buffer, buffer should not contain stacks
        let noStacks = false;

        // Check if a stack is already in view and needs to be finished & by how much
        const currentTopSymbol = SlotDefinition.getSymbolDefinition(wrapIndex(this.topSymbolIndex, this.reelstrip));
        this.log("MS:", this.reel.index, "TOP", this.topSymbolIndex, currentTopSymbol.id);
        if (currentTopSymbol.size > 1) {

            // Get remaining size of stack
            let remainingSize = currentTopSymbol.size - 1;
            for (let y = 1; y <= currentTopSymbol.size; y++) {
                const nextSymbol = SlotDefinition.getSymbolDefinition(wrapIndex(this.topSymbolIndex + y, this.reelstrip));
                if (nextSymbol.id === currentTopSymbol.id) {
                    remainingSize--;
                } else {
                    break;
                }
            }

            noStacks = true;
            randomBufferEnd = incoming.length - remainingSize;

            // Finish off stack
            while (remainingSize > 0) {
                incoming.splice(incoming.length - remainingSize, 1, currentTopSymbol.id);
                remainingSize--;
            }

            this.log("MS:", this.reel.index, "Finishing off stack", incoming);
        }

        // As above but for the result
        const bottomResultSymbol = SlotDefinition.getSymbolDefinition(incoming[this.reel.size]);
        this.log("MS:", this.reel.index, "BOTTOM", bottomResultSymbol.id);
        if (bottomResultSymbol.size > 1) {

            // Get remaining size of stack
            let remainingSize = bottomResultSymbol.size - 1;
            for (let y = 1; y < bottomResultSymbol.size; y++) {
                const nextSymbol = SlotDefinition.getSymbolDefinition(incoming[this.reel.size - y]);
                if (nextSymbol.id === bottomResultSymbol.id) {
                    remainingSize--;
                } else {
                    break;
                }
            }

            noStacks = true;
            randomBufferStart = this.reel.size + remainingSize + 1;

            this.log("MS:", this.reel.index, "Adding in remaining wilds below result", remainingSize);

            // Finish off stack
            while (remainingSize > 0) {
                incoming.splice(this.reel.size + remainingSize, 1, bottomResultSymbol.id);
                remainingSize--;
            }

            this.log("MS:", this.reel.index, "Introducing stack", incoming);
        }

        this.log("MS:", this.reel.index, "FILLING IN BUFFER", randomBufferStart, randomBufferEnd, incoming);

        // Fill remaining space with non-wild result symbols
        const symbolPool = [...this.result.reelstrip];
        let r = Math.floor(Math.random() * symbolPool.length);
        for (let y = randomBufferStart; y < randomBufferEnd; y++) {
            if (r > symbolPool.length - 1) {
                r--;
            }
            const symbol = symbolPool.splice(r, 1)[0];
            const symbolDef = SlotDefinition.getSymbolDefinition(symbol);

            if (!noStacks || symbolDef.size === 1) {
                // Stacks are allowed in buffer, but can only be complete stacks
                if (symbolDef.size > 1) {
                    if (y + symbolDef.size < randomBufferEnd) {
                        // Inserting a stack into buffer
                        noStacks = true;
                        for (let stackY = y; stackY < y + symbolDef.size; stackY++) {
                            incoming[stackY] = symbol;
                        }
                        this.log("MS:", this.reel.index, "FILL IN", symbol, symbolDef.size, incoming);
                        y += symbolDef.size - 1;
                    } else {
                        // Stack won't fit, try next symbol
                        y--;
                    }
                } else {
                    // Suitable symbol found
                    incoming[y] = symbol;
                    this.log("MS:", this.reel.index, "FILL IN", symbol, incoming);
                }
            } else {
                // Symbol not suitable, try next
                y--;
            }
        }

        this.log("MS:", this.reel.index, "Final incoming", incoming);
    }

    private updateSymbolPositions() {
        if(this.currentOrientation != canvasManager.orientation){
            this.getSymbolPositions();
            this.currentOrientation = canvasManager.orientation;
        }
        let reelIndex = Math.floor(this.currentPosition);
        let offset = 1 - (this.currentPosition - reelIndex);

        if (offset === 1) {
            offset = 0;
            reelIndex--;
        }

        this.topSymbolIndex = reelIndex;

        for (let i = 0; i < this.allSymbols.length; i++) {
            const symbol = this.allSymbols[i];
            const position = this.symbolPositions[i].clone();
            const nextPosition = this.symbolPositions[i + 1].clone();

            const lerpedPosition = Rectangle.Lerp(position, nextPosition, offset);

            symbol.setTransform(lerpedPosition);
            symbol.setSymbol(wrapIndex(reelIndex + i - (ReelSpinner.extended ? 1 : 0), this.reelstrip));
        }
    }

    private addHiddenSymbols() {
        // Create hidden symbol
        this.topHiddenSymbol = new SymbolComponent(new Point(this.reel.index, 0));

        this.allSymbols = [this.topHiddenSymbol, ...this.reel.getSymbols()];

        if (ReelSpinner.extended) {
            this.topExtendedHiddenSymbol = new SymbolComponent(new Point(this.reel.index, -1));
            this.bottomHiddenSymbol = new SymbolComponent(new Point(this.reel.index, this.reel.size + 1));

            this.allSymbols.unshift(this.topExtendedHiddenSymbol);
            this.allSymbols.push(this.bottomHiddenSymbol);
        }
    }

    private disableHiddenSymbols() {
        if (!ReelSpinner.extended) {
            this.topHiddenSymbol.setVisible(false);
            Components.matrix.removeHiddenSymbol(this.topHiddenSymbol);
        }
    }

    private enableHiddenSymbols() {
        this.topHiddenSymbol.setVisible(true);
        Components.matrix.addHiddenSymbol(this.topHiddenSymbol);
        if (ReelSpinner.extended) {
            this.topExtendedHiddenSymbol.setVisible(true);
            Components.matrix.addHiddenSymbol(this.topExtendedHiddenSymbol);
            this.bottomHiddenSymbol.setVisible(true);
            Components.matrix.addHiddenSymbol(this.bottomHiddenSymbol);
        }
    }

    private getSymbolPositions() {
        const topHiddenSymbolPosition = Layers.MatrixContent.getPosition(`top_hidden_symbol_${this.reel.index}`);
        const bottomHiddenSymbolPosition = Layers.MatrixContent.getPosition(`bottom_hidden_symbol_${this.reel.index}`);

        // Get positions of standard symbols

        this.symbolPositions = [];
        this.symbolPositions.push(topHiddenSymbolPosition);

        for (const symbol of this.reel.getSymbols()) {
            const x = symbol.gridPosition.x;
            const y = symbol.gridPosition.y;

            const positionRect = Layers.MatrixContent.getPosition(`symbol_${x}_${y}`);
            this.symbolPositions.push(positionRect);
        }

        this.symbolPositions.push(bottomHiddenSymbolPosition);

        if (ReelSpinner.extended) {
            const extendedBottomHiddenSymbolPosition = Layers.MatrixContent.getPosition(`extended_bottom_hidden_symbol_${this.reel.index}`);
            const extendedTopHiddenSymbolPosition = Layers.MatrixContent.getPosition(`extended_top_hidden_symbol_${this.reel.index}`);

            this.symbolPositions.unshift(extendedTopHiddenSymbolPosition);
            this.symbolPositions.push(extendedBottomHiddenSymbolPosition);
        }
    }

    private getAnticipationLayers() {
        const anticipationLayers = [];
        if (!ReelSpinner.anticipationLayer) {
            anticipationLayers.push(Layers.SymbolAnimations);
        } else if (ReelSpinner.anticipationLayer instanceof Layers) {
            anticipationLayers.push(ReelSpinner.anticipationLayer);
        } else {
            for (const layer of ReelSpinner.anticipationLayer) {
                anticipationLayers.push(layer);
            }
        }

        return anticipationLayers;
    }

    private log(...args: any[]) {
        // Logger.info("%c Reel spinner ", "background: #f4e242; color: #333", ...args);
    }

}

interface SpinStage {
    name: string;
    s: number;
    u: number;
    v: number;
    a: number;
    t: number;
}



// WEBPACK FOOTER //
// ./~/slotworks/components/matrix/reel/transition-behaviours/spin/reel-spinner.ts